/* Feb-2026 · Inventory Volume panel
 *
 * Unit-count overlay on top of the money-side inventory accounts
 * (13000 Merchandise + 13500 POD Titles). Bookkeeper records stock
 * IN or OUT — server posts the JE in the money ledger and stores the
 * qty on each line. This panel then aggregates the on-hand count.
 * Backend: GET /admin/gl/inventory/on-hand, POST /admin/gl/inventory/move
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PanelHeader } from './_shared';
import { Package, Plus, Minus, Loader2, RefreshCw } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });
const fmtUsd = (n) => `$${(Number(n) || 0).toFixed(2)}`;

function MoveForm({ onSaved }) {
  const [sku, setSku] = useState('');
  const [name, setName] = useState('');
  const [gl, setGl] = useState('13000');
  const [direction, setDirection] = useState('in');
  const [qty, setQty] = useState(1);
  const [unitCost, setUnitCost] = useState('');
  const today = new Date().toISOString().slice(0, 10);
  const [occurredOn, setOccurredOn] = useState(today);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!sku.trim()) { toast.error('SKU is required.'); return; }
    const q = Math.abs(parseInt(qty, 10) || 0);
    if (q <= 0) { toast.error('Quantity must be > 0.'); return; }
    const cost = parseFloat(unitCost);
    if (!(cost > 0)) { toast.error('Unit cost must be > $0.'); return; }
    setBusy(true);
    try {
      await http.post('/admin/gl/inventory/move', {
        sku: sku.trim(),
        name: name.trim(),
        gl_account: gl,
        qty_delta: direction === 'in' ? q : -q,
        unit_cost_usd: cost,
        occurred_on: occurredOn,
        notes: notes.trim(),
      });
      toast.success(`Booked ${direction.toUpperCase()} ${q} × ${sku} @ ${fmtUsd(cost)}`);
      setSku(''); setName(''); setNotes(''); setUnitCost('');
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed.');
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-md border border-border bg-card p-4" data-testid="inventory-move-form">
      <div className="mb-3 text-sm font-semibold">Record stock movement</div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          <Label className="text-xs">SKU</Label>
          <Input value={sku} onChange={(e) => setSku(e.target.value)} placeholder="TEE-BLK-M" data-testid="inventory-sku" />
        </div>
        <div>
          <Label className="text-xs">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Black tee — Medium" data-testid="inventory-name" />
        </div>
        <div>
          <Label className="text-xs">Account</Label>
          <select
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={gl}
            onChange={(e) => setGl(e.target.value)}
            data-testid="inventory-gl"
          >
            <option value="13000">13000 · Inventory — Merch</option>
            <option value="13500">13500 · Inventory — POD Titles</option>
          </select>
        </div>
        <div>
          <Label className="text-xs">Direction</Label>
          <div className="mt-1 flex gap-2">
            <Button size="sm" variant={direction === 'in' ? 'default' : 'outline'} onClick={() => setDirection('in')} data-testid="inventory-dir-in">
              <Plus className="mr-1 h-3.5 w-3.5" /> Stock IN
            </Button>
            <Button size="sm" variant={direction === 'out' ? 'default' : 'outline'} onClick={() => setDirection('out')} data-testid="inventory-dir-out">
              <Minus className="mr-1 h-3.5 w-3.5" /> Stock OUT
            </Button>
          </div>
        </div>
        <div>
          <Label className="text-xs">Quantity</Label>
          <Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="inventory-qty" />
        </div>
        <div>
          <Label className="text-xs">Unit cost (USD)</Label>
          <Input type="number" min="0" step="0.01" value={unitCost} onChange={(e) => setUnitCost(e.target.value)} placeholder="12.50" data-testid="inventory-cost" />
        </div>
        <div>
          <Label className="text-xs">Occurred on</Label>
          <Input type="date" value={occurredOn} onChange={(e) => setOccurredOn(e.target.value)} data-testid="inventory-date" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Notes</Label>
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Supplier / batch / lot #" data-testid="inventory-notes" />
        </div>
      </div>
      <div className="mt-3 flex justify-end">
        <Button size="sm" onClick={submit} disabled={busy} data-testid="inventory-save-btn">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Book movement
        </Button>
      </div>
    </div>
  );
}

export default function InventoryPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const r = await http.get('/admin/gl/inventory/on-hand');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Inventory load failed.');
    } finally { setBusy(false); }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  return (
    <div className="space-y-4" data-testid="inventory-panel">
      <PanelHeader
        icon={Package}
        title="Inventory Volume"
        subtitle="Unit counts on top of the money-side inventory accounts (13000 Merchandise + 13500 POD Titles). Every move books a matching JE."
      />
      <Button size="sm" variant="outline" onClick={reload} disabled={busy} data-testid="inventory-refresh">
        {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />} Refresh
      </Button>

      <MoveForm onSaved={reload} />

      {data && (
        <>
          <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
            {Object.entries(data.totals).map(([gl, t]) => (
              <div key={gl} className="rounded-md border border-border bg-card p-3">
                <div className="text-[11px] uppercase text-muted-foreground">{data.accounts[gl]}</div>
                <div className="mt-1 text-lg font-semibold" data-testid={`inventory-total-${gl}`}>{t.units.toLocaleString()} units</div>
                <div className="text-[12px] text-muted-foreground">{fmtUsd(t.value)} book value · {t.sku_count} SKU(s)</div>
              </div>
            ))}
          </div>

          <div className="overflow-hidden rounded-md border border-border bg-card">
            <table className="w-full text-[12px]" data-testid="inventory-table">
              <thead className="bg-panel-header/40 text-left text-[10.5px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2">SKU</th>
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Account</th>
                  <th className="px-3 py-2 text-right">In</th>
                  <th className="px-3 py-2 text-right">Out</th>
                  <th className="px-3 py-2 text-right">On hand</th>
                  <th className="px-3 py-2 text-right">Book value</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">No inventory moves yet.</td></tr>
                )}
                {data.rows.map((r) => (
                  <tr key={`${r.gl_account}-${r.sku}`} className="border-t border-border" data-testid={`inventory-row-${r.sku}`}>
                    <td className="px-3 py-2 font-mono-stat">{r.sku}</td>
                    <td className="px-3 py-2">{r.name || '—'}</td>
                    <td className="px-3 py-2 text-[11px] text-muted-foreground">{r.gl_account}</td>
                    <td className="px-3 py-2 text-right">{r.qty_in}</td>
                    <td className="px-3 py-2 text-right">{r.qty_out}</td>
                    <td className={`px-3 py-2 text-right font-semibold ${r.on_hand <= 0 ? 'text-rose-700' : r.on_hand < 10 ? 'text-amber-700' : ''}`}>{r.on_hand}</td>
                    <td className="px-3 py-2 text-right font-mono-stat">{fmtUsd(r.book_value_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
